import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  if (typeof detail === "object") {
    return Object.entries(detail)
      .map(([key, value]) => (Array.isArray(value) ? `${key}: ${value.join(" ")}` : `${key}: ${value}`))
      .join(" ");
  }
  return String(detail);
}

function formatAxiosError(error) {
  const fromBody =
    formatApiErrorDetail(error.response?.data?.detail) ||
    formatApiErrorDetail(error.response?.data);
  if (fromBody) return fromBody;
  if (error.message === "Network Error") {
    return "Cannot reach the server. Please try again.";
  }
  return error.message || "Something went wrong. Please try again.";
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not auth
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  // Silent token refresh every 12 minutes
  useEffect(() => {
    if (!user || user === false) return;
    const interval = setInterval(async () => {
      try {
        await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
      } catch { /* ignore refresh failure */ }
    }, 12 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user]);

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      setUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatAxiosError(e) };
    }
  };

  const register = async (name, email, password, company) => {
    try {
      const { data } = await axios.post(`${API}/auth/register`, { name, email, password, company }, { withCredentials: true });
      if (data.requires_verification) {
        return { success: false, requires_verification: true, email: data.email };
      }
      setUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatAxiosError(e) };
    }
  };

  const verifyEmailToken = async (token, email) => {
    try {
      const { data } = await axios.get(`${API}/auth/verify-email?token=${token}&email=${encodeURIComponent(email)}`, { withCredentials: true });
      if (data.token) {
        setUser(data);
      }
      return { success: true, message: data.message, already_verified: data.already_verified };
    } catch (e) {
      return { success: false, error: formatAxiosError(e) };
    }
  };

  const resendVerification = async (email) => {
    try {
      await axios.post(`${API}/auth/resend-verification`, { email });
      return { success: true };
    } catch (e) {
      return { success: false, error: formatAxiosError(e) };
    }
  };

  const acceptHrInvite = async (token, name, password) => {
    try {
      const { data } = await axios.post(
        `${API}/auth/join-hr/accept`,
        { token, name, password },
        { withCredentials: true },
      );
      setUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatAxiosError(e) };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch { /* ignore */ }
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, verifyEmailToken, resendVerification, acceptHrInvite, refreshUser: checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
